import { useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, ArrowDownToLine } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useData } from "../lib/DataContext";
import { PageHeader, Panel, TierBadge } from "../components/shared";
import { dupExtSkuIds } from "../components/factsheet/common";

export const BULK_FIELDS = [
  { k: "name", label: "Name", type: "text" },
  { k: "sku", label: "SKU", type: "text" },
  { k: "external_sku", label: "External SKU", type: "text", tiers: [1] },
  { k: "category", label: "Category", type: "text" },
  { k: "unit", label: "Unit", type: "text" },
  { k: "stock", label: "On Hand (count adjust)", type: "num" },
  { k: "par_level", label: "Par Level", type: "num" },
  { k: "unit_cost", label: "Unit Cost ($)", type: "num", ok: (it) => it.tier === 1 || it.manual_cost_override },
  { k: "order_unit_qty", label: "Order Pack Qty", type: "num", tiers: [1] },
  { k: "supplier_id", label: "Supplier", type: "supplier", tiers: [1] },
  { k: "storage_location", label: "Storage / Bin", type: "text", tiers: [1, 2] },
  { k: "shopify_sku", label: "Shopify SKU", type: "text", tiers: [3] },
  { k: "selling_price", label: "Selling Price ($)", type: "num", tiers: [3] },
  { k: "notes", label: "Notes", type: "text" },
];
export const BULK_KEY = "flowforge-bulk-edit";
const applies = (f, it) => (f.ok ? f.ok(it) : !f.tiers || f.tiers.includes(it.tier));
const show = (v) => (v === null || v === undefined ? "" : String(v));
const cellCls = "h-11 w-full px-2 bg-[#090D16] border rounded-md text-sm focus:outline-none focus:border-[#F59E0B]";

function Cell({ f, it, value, changed, onChange, suppliers }) {
  if (!applies(f, it)) return <div className="h-11 grid place-items-center text-[#334155] font-mono text-xs" data-testid={`bulk-na-${f.k}-${it.sku}`}>N/A</div>;
  const cls = `${cellCls} ${changed ? "border-[#F59E0B] bg-[#F59E0B]/5" : "border-[#334155]"} ${f.type === "num" ? "font-mono text-right" : ""}`;
  if (f.type === "supplier") return (
    <select data-testid={`bulk-cell-${f.k}-${it.sku}`} className={cls} value={value || ""} onChange={(e) => onChange(e.target.value)}>
      <option value="">— none —</option>{suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
    </select>
  );
  return <input data-testid={`bulk-cell-${f.k}-${it.sku}`} type={f.type === "num" ? "number" : "text"} step="any" className={cls} value={value} onChange={(e) => onChange(e.target.value)} />;
}

function FillAll({ f, onFill, suppliers }) {
  const [v, setV] = useState("");
  return (
    <div className="flex gap-1 mt-1.5">
      {f.type === "supplier" ? (
        <select data-testid={`bulk-fill-${f.k}`} value={v} onChange={(e) => setV(e.target.value)} className="h-8 flex-1 min-w-0 px-1 bg-[#090D16] border border-[#334155] rounded text-[11px] normal-case tracking-normal">
          <option value="">Set all…</option>{suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
      ) : <input data-testid={`bulk-fill-${f.k}`} value={v} onChange={(e) => setV(e.target.value)} placeholder="Set all…" type={f.type === "num" ? "number" : "text"}
        className="h-8 flex-1 min-w-0 px-2 bg-[#090D16] border border-[#334155] rounded text-[11px] normal-case tracking-normal font-sans" />}
      <button data-testid={`bulk-fill-apply-${f.k}`} title="Apply to every row" disabled={v === ""} onClick={() => onFill(v)}
        className="h-8 w-8 grid place-items-center rounded border border-[#334155] text-[#F59E0B] disabled:opacity-30"><ArrowDownToLine className="w-3.5 h-3.5" /></button>
    </div>
  );
}

export default function BulkEdit() {
  const { items, suppliers, refreshAll } = useData();
  const navigate = useNavigate();
  const sel = useMemo(() => { try { return JSON.parse(sessionStorage.getItem(BULK_KEY)) || null; } catch { return null; } }, []);
  const [snap] = useState(() => (sel ? items.filter((i) => sel.ids.includes(i.id)) : []));
  const fields = BULK_FIELDS.filter((f) => sel?.fields.includes(f.k));
  const editFields = fields.filter((f) => f.k !== "name");
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  const orig = (it, k) => show(it[k]);
  const val = (it, k) => (vals[it.id]?.[k] ?? orig(it, k));
  const setVal = (id, k, v) => setVals((p) => ({ ...p, [id]: { ...p[id], [k]: v } }));
  const fill = (f, v) => setVals((p) => { const n = { ...p }; snap.forEach((it) => { if (applies(f, it)) n[it.id] = { ...n[it.id], [f.k]: v }; }); return n; });
  const changes = snap.map((it) => {
    const ch = Object.entries(vals[it.id] || {}).filter(([k, v]) => v !== orig(it, k));
    return [it, ch];
  }).filter(([, ch]) => ch.length);
  const nChanged = changes.reduce((a, [, ch]) => a + ch.length, 0);

  const save = async () => {
    const rows = [];
    for (const [it, ch] of changes) {
      const row = { id: it.id, changes: {} };
      for (const [k, v] of ch) {
        const f = BULK_FIELDS.find((x) => x.k === k);
        if (f.type === "num") {
          if (v === "" || Number.isNaN(Number(v))) return toast.error(`${it.name}: ${f.label} must be a number`);
          if (k === "stock") row.stock = Number(v); else row.changes[k] = Number(v);
        } else if (k === "supplier_id") row.changes.supplier_id = v || "";
        else if ((k === "name" || k === "sku") && !v.trim()) return toast.error(`${it.name}: ${f.label} can't be blank`);
        else row.changes[k] = v.trim();
      }
      rows.push(row);
    }
    const byId = Object.fromEntries(rows.map((r) => [r.id, r.changes]));
    const after = items.map((i) => ({ ...i, ...(byId[i.id] || {}) }));
    const dupIds = dupExtSkuIds(after);
    const hit = rows.filter((r) => ("external_sku" in r.changes || "supplier_id" in r.changes) && dupIds.has(r.id)).map((r) => after.find((i) => i.id === r.id));
    if (hit.length && !window.confirm(`These items would share an external SKU with another item from the same supplier:\n${hit.map((i) => `${i.name} → ${i.external_sku}`).join("\n")}\n\nSave anyway?`)) return;
    setBusy(true);
    try { await api.post("/items/bulk", rows); await refreshAll(); toast.success(`Saved ${nChanged} change${nChanged === 1 ? "" : "s"} across ${rows.length} item${rows.length === 1 ? "" : "s"}`); navigate("/items"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (!sel || !snap.length) return (
    <div><PageHeader kicker="// ADMIN · DIRECTORY" title="Bulk Edit" />
      <Panel className="p-8 text-center font-mono text-[#6B7280]" data-testid="bulk-empty">NOTHING SELECTED · <Link to="/items" className="text-[#F59E0B]">BACK TO ITEM MASTER</Link></Panel></div>
  );
  return (
    <div data-testid="bulk-edit-page">
      <PageHeader kicker={`// ADMIN · ${snap.length} ITEMS · ${editFields.length} FIELDS`} title="Bulk Edit">
        <button data-testid="bulk-back-btn" onClick={() => navigate("/items")} className="h-12 px-4 rounded-md border border-[#334155] font-semibold flex items-center gap-2"><ArrowLeft className="w-4 h-4" />Cancel</button>
        <button data-testid="bulk-save-btn" disabled={!nChanged || busy} onClick={save} className="h-12 px-5 rounded-md bg-[#F59E0B] text-[#090D16] font-bold flex items-center gap-2 disabled:opacity-40">
          <Save className="w-4 h-4" />{busy ? "Saving…" : `Save ${nChanged || ""} change${nChanged === 1 ? "" : "s"}`}</button>
      </PageHeader>
      {sel.fields.includes("stock") && <div className="mb-3 text-xs text-[#9CA3AF]">On Hand changes are logged as count adjustments in the Audit Log.</div>}
      <Panel className="overflow-auto max-h-[74vh]" data-testid="bulk-table">
        <table className="text-sm border-separate border-spacing-0">
          <thead><tr className="font-mono text-[10px] tracking-[0.15em] text-[#6B7280] uppercase">
            <th className="sticky left-0 top-0 z-30 bg-[#0B111D] px-4 py-3 text-left min-w-[240px] border-b border-r border-[#1E293B]">Name</th>
            {editFields.map((f) => <th key={f.k} data-testid={`bulk-col-${f.k}`} className="sticky top-0 z-20 bg-[#0B111D] px-2 py-3 text-left min-w-[180px] align-top border-b border-[#1E293B]">{f.label}<FillAll f={f} suppliers={suppliers} onFill={(v) => fill(f, v)} /></th>)}
          </tr></thead>
          <tbody>
            {snap.map((it) => (
              <tr key={it.id} data-testid={`bulk-row-${it.sku}`}>
                <td className="sticky left-0 z-10 bg-[#0F1623] px-4 py-2 min-w-[240px] border-b border-r border-[#1E293B]">
                  <div className="flex items-center gap-2"><TierBadge tier={it.tier} />
                    {sel.fields.includes("name")
                      ? <input data-testid={`bulk-cell-name-${it.sku}`} value={val(it, "name")} onChange={(e) => setVal(it.id, "name", e.target.value)} className={`${cellCls} ${val(it, "name") !== orig(it, "name") ? "border-[#F59E0B]" : "border-[#334155]"}`} />
                      : <span className="font-semibold truncate max-w-[200px]" data-testid={`bulk-name-${it.sku}`}>{it.name}</span>}
                  </div>
                  <div className="font-mono text-[10px] text-[#6B7280] mt-0.5">{it.sku}</div>
                </td>
                {editFields.map((f) => (
                  <td key={f.k} className="px-2 py-2 border-b border-[#1E293B]">
                    <Cell f={f} it={it} value={val(it, f.k)} changed={val(it, f.k) !== orig(it, f.k)} onChange={(v) => setVal(it.id, f.k, v)} suppliers={suppliers} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}
